import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const Container = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const SectionTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: var(--gray-900);
`;

const AdminNav = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const AdminNavLink = styled(Link)`
  padding: 0.75rem 1.5rem;
  background-color: var(--gray-200);
  color: var(--gray-700);
  border-radius: var(--border-radius-md);
  font-weight: 500;
  text-decoration: none;
  transition: all var(--transition-fast) ease-in-out;
  
  &:hover {
    background-color: var(--gray-300);
  }
  
  &.active {
    background-color: var(--primary-color);
    color: white;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  min-width: 250px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  background-color: white;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${props => props.variant === 'danger' ? 'var(--danger-color)' : 'var(--primary-color)'};
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-fast) ease-in-out;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:disabled {
    background-color: var(--gray-400);
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  background: var(--white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--box-shadow-sm);
  overflow: hidden;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  background-color: var(--gray-100);
  font-weight: 600;
  color: var(--gray-700);
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid var(--gray-200);
`;

const CATEGORY_COLORS = {
  '软件产品': 'var(--primary-color)',
  '硬件设备': 'var(--secondary-color)',
  '智能硬件': '#10b981',
  '物联网设备': '#f59e0b',
  '通信设备': '#8b5cf6',
  '安防设备': '#ef4444',
  '系统软件': '#3b82f6',
  '应用软件': '#06b6d4',
  '平台服务': '#ec4899',
  '数据服务': '#84cc16'
};

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${props => CATEGORY_COLORS[props.type] || 'var(--primary-color)'};
  color: white;
  border-radius: var(--border-radius-full);
  font-size: 0.75rem;
  font-weight: 500;
`;

const StockBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: ${props => props.inStock ? 'var(--success-light)' : 'var(--danger-light)'};
  color: ${props => props.inStock ? 'var(--success-dark)' : 'var(--danger-dark)'};
  border-radius: var(--border-radius-md);
  font-size: 0.75rem;
  font-weight: 500;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-md);
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => 
    props.$status === 'active' ? 'var(--success-light)' : 
    props.$status === 'draft' ? '#fef3c7' : 'var(--danger-light)'};
  color: ${props => 
    props.$status === 'active' ? 'var(--success-dark)' : 
    props.$status === 'draft' ? '#92400e' : 'var(--danger-dark)'};
`;

const ActionButton = styled.button`
  padding: 0.4rem 0.75rem;
  margin-right: 0.5rem;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 0.75rem;
  cursor: pointer;
  transition: all var(--transition-fast) ease-in-out;
  
  &.edit {
    background-color: var(--primary-light);
    color: white;
  }
  
  &.delete {
    background-color: var(--danger-light);
    color: var(--danger-dark);
  }
  
  &:hover {
    opacity: 0.8;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--gray-500);
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--gray-300);
  background-color: ${props => props.active ? 'var(--primary-color)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--gray-700)'};
  border-radius: var(--border-radius-md);
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--gray-100)'};
  }
  
  &:disabled {
    background-color: var(--gray-100);
    cursor: not-allowed;
  }
`;

const Stats = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: var(--white);
  padding: 1.25rem 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--box-shadow-sm);
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
`;

const StatLabel = styled.span`
  font-size: 0.875rem;
  color: var(--gray-600);
`;

// 批量选择相关样式
const BatchActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 1rem;
  background: #e7f3ff;
  border-radius: 8px;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const SelectedCount = styled.span`
  font-weight: 500;
  color: var(--primary-color);
  margin-right: 0.5rem;
`;

const SelectionDropdown = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid var(--gray-300);
  border-radius: var(--border-radius-md);
  background: white;
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const ConfirmModalContent = styled.div`
  background: var(--white);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  max-width: 500px;
  width: 90%;
`;

const WarningIcon = styled.div`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const ConfirmMessage = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  color: var(--gray-800);
`;

const ConfirmDetails = styled.div`
  background: var(--gray-100);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  margin-bottom: 1.5rem;
  max-height: 200px;
  overflow-y: auto;
`;

const ConfirmDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--gray-200);
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
  }
`;

const DeleteProgress = styled.div`
  margin-top: 1rem;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: var(--gray-200);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--danger-color) 0%, #c82333 100%);
  transition: width 0.3s ease;
  width: ${props => props.percent}%;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: var(--gray-600);
`;

const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  background: ${props => props.$type === 'success' ? 'var(--success-color)' : props.$type === 'error' ? 'var(--danger-color)' : 'var(--primary-color)'};
  color: white;
  border-radius: var(--border-radius-md);
  box-shadow: var(--box-shadow-md);
  z-index: 9999;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PRODUCT_CATEGORIES = [
  '软件产品', 
  '硬件设备', 
  '智能硬件', 
  '物联网设备', 
  '通信设备', 
  '安防设备', 
  '系统软件', 
  '应用软件', 
  '平台服务', 
  '数据服务'
];

// 去除 HTML 标签，提取纯文本
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const text = doc.body.textContent || '';
  return text.trim();
};

const AdminProducts = () => {
  const navigate = useNavigate();

  // 当前登录者角色：供应商只能看到并维护自己上传的产品
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('adminUser') || '{}');
    } catch (e) {
      return {};
    }
  })();
  const isSupplier = currentUser.role === 'supplier';

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [auditStatus, setAuditStatus] = useState('all');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0
  });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [batchDeleteProgress, setBatchDeleteProgress] = useState({ current: 0, total: 0, deleting: false });
  const [selectFilter, setSelectFilter] = useState('all');

  // Toast 提示
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchProducts = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page,
        limit: 20
      };
      
      if (category !== 'all') {
        params.category = category;
      }
      
      if (search.trim()) {
        params.search = search.trim();
      }

      // 我司管理人员可按审核状态筛选
      if (!isSupplier && auditStatus !== 'all') {
        params.auditStatus = auditStatus;
      }
      
      const token = localStorage.getItem('adminToken');
      // 供应商走供应商接口（服务端强制只返回自己名下产品）
      const url = isSupplier ? '/api/supplier/products' : '/api/admin/products';
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setProducts(response.data.products || []);
      setPagination({
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1,
        total: response.data.total || 0
      });
    } catch (err) {
      // 带上具体原因，便于区分「登录失效」「权限不足」「网络/证书问题」
      const status = err.response && err.response.status;
      const serverMsg = err.response && err.response.data && err.response.message;
      if (status === 401) {
        setError('登录已失效，请重新登录后台');
      } else if (status === 403) {
        setError('当前账号权限不足（需要管理员权限）');
      } else if (status) {
        setError(`获取产品列表失败（HTTP ${status}）`);
      } else {
        setError('获取产品列表失败：网络异常或证书不受信任');
      }
      console.error('获取产品失败:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, auditStatus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (category || search) {
        fetchProducts(1);
      }
    }, 500);
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts(1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这个产品吗？')) {
      return;
    }
    
    try {
      // 供应商删除走供应商接口（已通过审核的产品服务端会拒绝删除）
      const url = isSupplier ? `/api/supplier/products/${id}` : `/api/admin/products/${id}`;
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      showToast('产品删除成功');
      fetchProducts(pagination.currentPage);
    } catch (err) {
      showToast(err.response?.data?.message || '删除产品失败', 'error');
    }
  };

  // 审核通过（我司管理人员）
  const handleApprove = async (id) => {
    try {
      await axios.post(
        `/api/admin/products/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      );
      showToast('审核通过，产品已展示');
      fetchProducts(pagination.currentPage);
    } catch (err) {
      showToast(err.response?.data?.message || '审核失败', 'error');
    }
  };

  // 审核驳回（我司管理人员）
  const handleReject = async (id) => {
    const remark = window.prompt('请填写驳回原因（供应商可见）：');
    if (remark === null) return;
    if (!remark.trim()) {
      showToast('请填写驳回原因', 'error');
      return;
    }
    try {
      await axios.post(
        `/api/admin/products/${id}/reject`,
        { remark: remark.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      );
      showToast('已驳回');
      fetchProducts(pagination.currentPage);
    } catch (err) {
      showToast(err.response?.data?.message || '驳回失败', 'error');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedProducts.length === 0) {
      showToast('请先选择要删除的产品', 'error');
      return;
    }

    setShowBatchDeleteModal(true);
    setBatchDeleteProgress({ current: 0, total: selectedProducts.length, deleting: false });
  };

  const confirmBatchDelete = async () => {
    if (selectedProducts.length === 0) return;

    setBatchDeleteProgress({ current: 0, total: selectedProducts.length, deleting: true });

    try {
      // 分批删除，每批10条
      const batchSize = 10;
      let deletedCount = 0;
      const totalBatches = Math.ceil(selectedProducts.length / batchSize);

      for (let i = 0; i < totalBatches; i++) {
        const batch = selectedProducts.slice(i * batchSize, (i + 1) * batchSize);
        
        for (const id of batch) {
          await axios.delete(`/api/admin/products/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
          });
          deletedCount++;
          setBatchDeleteProgress({ 
            current: deletedCount, 
            total: selectedProducts.length, 
            deleting: true 
          });
        }
      }

      setShowBatchDeleteModal(false);
      showToast(`成功删除 ${deletedCount} 个产品`);
      setSelectedProducts([]);
      setSelectFilter('all');
      fetchProducts(1);
    } catch (err) {
      console.error('批量删除失败:', err);
      showToast(err.response?.data?.message || '批量删除失败', 'error');
      setShowBatchDeleteModal(false);
    }
  };

  // 全选/取消全选
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProducts(products.map(p => p._id));
    } else {
      setSelectedProducts([]);
    }
  };

  // 选择单个
  const handleSelect = (id) => {
    if (selectedProducts.includes(id)) {
      setSelectedProducts(selectedProducts.filter(i => i !== id));
    } else {
      setSelectedProducts([...selectedProducts, id]);
    }
  };

  // 按分类筛选选择
  const handleSelectByCategory = (cat) => {
    if (cat === 'all') {
      setSelectedProducts(products.map(p => p._id));
    } else {
      setSelectedProducts(products.filter(p => p.category === cat).map(p => p._id));
    }
    setSelectFilter(cat);
  };

  // 快捷键支持
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + A: 全选当前页
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        setSelectedProducts(products.map(p => p._id));
      }
      // Delete: 批量删除选中项
      if (e.key === 'Delete' && selectedProducts.length > 0 && !['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {
        e.preventDefault();
        handleBatchDelete();
      }
      // Escape: 取消选择
      if (e.key === 'Escape' && selectedProducts.length > 0) {
        setSelectedProducts([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, selectedProducts]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchProducts(newPage);
    }
  };

  const softwareCount = products.filter(p => p.category === '软件产品').length;
  const hardwareCount = products.filter(p => p.category === '硬件设备').length;
  const allSelected = products.length > 0 && selectedProducts.length === products.length;

  return (
    <AdminLayout title="产品管理">
      <Container>
        <SectionTitle>{isSupplier ? '我的产品' : '产品管理'}</SectionTitle>
        <AdminNav>
          <AdminNavLink to="/admin/products" className="active">产品列表</AdminNavLink>
          <AdminNavLink to="/admin/products/new">添加产品</AdminNavLink>
        </AdminNav>

        {isSupplier && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            background: '#fff3cd',
            color: '#856404',
            borderRadius: '4px',
            fontSize: '0.85rem'
          }}>
            您新增或修改的产品需经我司管理人员审核通过后，才会展示在官网与小程序商城。
          </div>
        )}

        <Stats>
          <StatCard>
            <StatValue>{pagination.total}</StatValue>
            <StatLabel>全部产品</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{softwareCount}</StatValue>
            <StatLabel>软件产品</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{hardwareCount}</StatValue>
            <StatLabel>硬件设备</StatLabel>
          </StatCard>
        </Stats>
        
        <FilterBar>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem' }}>
            <SearchInput
              type="text"
              placeholder="搜索产品名称或描述..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button type="submit">搜索</Button>
          </form>
          
          {!isSupplier && (
            <Select
              value={auditStatus}
              onChange={(e) => setAuditStatus(e.target.value)}
            >
              <option value="all">全部审核状态</option>
              <option value="pending">待审核</option>
              <option value="approved">已通过</option>
              <option value="rejected">已驳回</option>
            </Select>
          )}

          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="all">全部分类</option>
            {PRODUCT_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </Select>
          
          <Button onClick={() => { setSearch(''); setCategory('all'); fetchProducts(1); }}>
            重置筛选
          </Button>
        </FilterBar>
        
        {error && (
          <div style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>{error}</div>
        )}
        
        {/* 批量操作栏 */}
        {selectedProducts.length > 0 && (
          <BatchActions>
            <SelectedCount>已选择 {selectedProducts.length} 项</SelectedCount>
            
            {/* 按分类快速选择 */}
            <span style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>快速选择:</span>
            <SelectionDropdown value={selectFilter} onChange={(e) => handleSelectByCategory(e.target.value)}>
              <option value="all">全部 {products.length} 条</option>
              <option value="软件产品">软件产品 ({products.filter(p => p.category === '软件产品').length})</option>
              <option value="硬件设备">硬件设备 ({products.filter(p => p.category === '硬件设备').length})</option>
              <option value="智能硬件">智能硬件 ({products.filter(p => p.category === '智能硬件').length})</option>
              <option value="物联网设备">物联网设备 ({products.filter(p => p.category === '物联网设备').length})</option>
              <option value="通信设备">通信设备 ({products.filter(p => p.category === '通信设备').length})</option>
              <option value="安防设备">安防设备 ({products.filter(p => p.category === '安防设备').length})</option>
            </SelectionDropdown>
            
            <Button variant="danger" onClick={handleBatchDelete}>🗑️ 批量删除</Button>
            <Button onClick={() => { setSelectedProducts([]); setSelectFilter('all'); }}>取消选择</Button>
            
            {/* 快捷键提示 */}
            <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginLeft: '0.5rem' }}>
              快捷键: Ctrl+A 全选 | Delete 删除 | Esc 取消
            </span>
          </BatchActions>
        )}
        
        {loading ? (
          <EmptyState>加载中...</EmptyState>
        ) : products.length === 0 ? (
          <EmptyState>
            <p>暂无产品数据</p>
            <Button onClick={() => navigate('/admin/products/new')} style={{ marginTop: '1rem' }}>
              添加第一个产品
            </Button>
          </EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: '40px' }}>
                    <Checkbox
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </Th>
                  <Th>产品名称</Th>
                  <Th>分类</Th>
                  <Th>价格</Th>
                  <Th>库存</Th>
                  <Th>状态</Th>
                  {!isSupplier && <Th>供应商</Th>}
                  {!isSupplier && <Th>分成比</Th>}
                  <Th>审核</Th>
                  <Th>标签</Th>
                  <Th>操作</Th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product._id} style={{ background: selectedProducts.includes(product._id) ? '#e7f3ff' : 'transparent' }}>
                    <Td>
                      <Checkbox
                        type="checkbox"
                        checked={selectedProducts.includes(product._id)}
                        onChange={() => handleSelect(product._id)}
                      />
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 500 }}>{product.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                        {stripHtml(product.description)?.substring(0, 50) || '暂无描述'}
                      </div>
                    </Td>
                    <Td>
                      <CategoryBadge type={product.category}>{product.category}</CategoryBadge>
                    </Td>
                    <Td>
                      {product.price > 0 ? `¥${product.price}` : '面议'}
                    </Td>
                    <Td>
                      <StockBadge inStock={product.inStock}>
                        {product.inStock ? `有货 (${product.stockQuantity})` : '缺货'}
                      </StockBadge>
                    </Td>
                    <Td>
                      <StatusBadge $status={product.status || 'active'}>
                        {product.status === 'active' ? '已发布' : product.status === 'draft' ? '草稿' : '已下架'}
                      </StatusBadge>
                    </Td>
                    {!isSupplier && (
                      <Td>
                        {product.supplier
                          ? (product.supplier.supplierInfo?.companyName || product.supplier.username || product.supplierName)
                          : '—'}
                      </Td>
                    )}
                    {!isSupplier && (
                      <Td>{product.commissionRate ? `${product.commissionRate}%` : '—'}</Td>
                    )}
                    <Td>
                      <span style={{
                        display: 'inline-block',
                        padding: '0.15rem 0.55rem',
                        borderRadius: '10px',
                        fontSize: '0.75rem',
                        background:
                          product.auditStatus === 'pending' ? '#fff3cd'
                            : product.auditStatus === 'rejected' ? '#f8d7da' : '#d4edda',
                        color:
                          product.auditStatus === 'pending' ? '#856404'
                            : product.auditStatus === 'rejected' ? '#721c24' : '#155724'
                      }}>
                        {product.auditStatus === 'pending' ? '待审核'
                          : product.auditStatus === 'rejected' ? '已驳回' : '已通过'}
                      </span>
                      {product.audit?.remark && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                          {product.audit.remark}
                        </div>
                      )}
                      {product.pendingData && (
                        <div style={{ fontSize: '0.7rem', color: '#856404', marginTop: '0.25rem' }}>
                          有变更待审核
                        </div>
                      )}
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {product.tags?.slice(0, 3).map((tag, index) => (
                          <span key={index} style={{
                            padding: '0.125rem 0.5rem',
                            backgroundColor: 'var(--gray-200)',
                            borderRadius: 'var(--border-radius-sm)',
                            fontSize: '0.75rem'
                          }}>
                            {tag}
                          </span>
                        ))}
                        {product.tags?.length > 3 && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                            +{product.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td>
                      {!isSupplier && (product.auditStatus === 'pending' || product.pendingData) && (
                        <>
                          <ActionButton
                            className="edit"
                            onClick={() => handleApprove(product._id)}
                          >
                            通过
                          </ActionButton>
                          <ActionButton
                            className="delete"
                            onClick={() => handleReject(product._id)}
                          >
                            驳回
                          </ActionButton>
                        </>
                      )}
                      <ActionButton
                        className="edit"
                        onClick={() => navigate(`/admin/products/edit/${product._id}`)}
                      >
                        编辑
                      </ActionButton>
                      <ActionButton
                        className="delete"
                        onClick={() => handleDelete(product._id)}
                      >
                        删除
                      </ActionButton>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {pagination.totalPages > 1 && (
              <Pagination>
                <PageButton
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.currentPage === 1}
                >
                  首页
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                >
                  上一页
                </PageButton>
                <PageButton disabled>
                  第 {pagination.currentPage} / {pagination.totalPages} 页
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  下一页
                </PageButton>
                <PageButton
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  末页
                </PageButton>
              </Pagination>
            )}
          </>
        )}

        {/* Toast 提示 */}
        {toast && <Toast $type={toast.type}>{toast.message}</Toast>}

        {/* 批量删除确认模态框 */}
        {showBatchDeleteModal && (
          <Modal onClick={() => !batchDeleteProgress.deleting && setShowBatchDeleteModal(false)}>
            <ConfirmModalContent onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>确认删除</h3>
                {!batchDeleteProgress.deleting && (
                  <button
                    onClick={() => setShowBatchDeleteModal(false)}
                    style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'var(--gray-500)' }}
                  >
                    &times;
                  </button>
                )}
              </div>
              
              <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                <WarningIcon>⚠️</WarningIcon>
                
                {!batchDeleteProgress.deleting ? (
                  <>
                    <ConfirmMessage>
                      确定要删除选中的 <strong>{selectedProducts.length}</strong> 个产品吗？
                    </ConfirmMessage>
                    <p style={{ textAlign: 'center', color: 'var(--danger-color)', marginBottom: '1rem' }}>
                      此操作不可撤销，删除后数据将无法恢复！
                    </p>
                    
                    <ConfirmDetails>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--gray-800)' }}>
                        即将删除的产品列表：
                      </div>
                      {products
                        .filter(p => selectedProducts.includes(p._id))
                        .slice(0, 10)
                        .map(p => (
                          <ConfirmDetailItem key={p._id}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.name}
                            </span>
                            <CategoryBadge type={p.category} style={{ marginLeft: '0.5rem', flexShrink: 0 }}>
                              {p.category}
                            </CategoryBadge>
                          </ConfirmDetailItem>
                        ))}
                      {selectedProducts.length > 10 && (
                        <div style={{ textAlign: 'center', color: 'var(--gray-500)', padding: '0.5rem', fontSize: '0.85rem' }}>
                          ...还有 {selectedProducts.length - 10} 条未显示
                        </div>
                      )}
                    </ConfirmDetails>
                    
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                      <Button onClick={() => setShowBatchDeleteModal(false)}>取消</Button>
                      <Button variant="danger" onClick={confirmBatchDelete}>确认删除</Button>
                    </div>
                  </>
                ) : (
                  <DeleteProgress>
                    <ConfirmMessage>正在删除...</ConfirmMessage>
                    <ProgressBar>
                      <ProgressFill percent={Math.round((batchDeleteProgress.current / batchDeleteProgress.total) * 100)} />
                    </ProgressBar>
                    <ProgressText>
                      {batchDeleteProgress.current} / {batchDeleteProgress.total} 个已删除
                    </ProgressText>
                  </DeleteProgress>
                )}
              </div>
            </ConfirmModalContent>
          </Modal>
        )}
      </Container>
    </AdminLayout>
  );
};

export default AdminProducts;